// Encart « Activité récente » du tableau de bord.
//
// Un clic sur une pastille de type recharge la carte ENTIÈRE côté serveur : la case à cocher
// fait partie de la réponse, pas du DOM courant. Rien ne bougeait donc à l'écran pendant tout
// l'aller-retour. On croyait avoir raté le clic, et on recliquait.
//
// Même remède que les onglets de scope d'un index (`index_scope_skeleton.ts`) : l'état de la
// pastille est posé tout de suite, et le fil passe en barres. Rien à défaire — la région
// squelettisée est exactement celle que Turbo remplace, échec de chargement compris
// (`showTurboFrameLoadError`, cf. entrypoints/common).

const CARD_SELECTOR = '.recent-activity';
const KIND_SELECTOR = 'a.recent-activity__kind';

// Sans fil à copier (sélection vide), on en invente juste assez pour que la carte ait un corps
// de la même hauteur que quatre lignes.
const PLACEHOLDER_ROWS = 4;

// Cases à cocher : seule celle qu'on clique change. La coche elle-même est un pur basculement
// de classe — les deux icônes sont rendues côté serveur, parce que Font Awesome remplace
// chaque <i> par un <svg> au tracé figé (cf. le gabarit du composant).
const markKindToggled = (link: HTMLElement) => {
  const nowOff = link.classList.toggle('recent-activity__kind--off');
  link.setAttribute('aria-pressed', String(!nowOff));
};

const bar = (className: string) => {
  const element = document.createElement('span');
  element.className = `recent-activity__bar is-loading ${className}`;
  return element;
};

// Le squelette est construit À PARTIR des lignes affichées : même nombre, même hauteur. La
// carte garde donc sa taille et le masonry ne saute pas pendant l'attente.
const skeletonizeRow = (row: HTMLElement, index: number) => {
  const width = ['long', 'medium', 'short'][index % 3];
  row.replaceChildren(bar('recent-activity__bar--icon'), bar(`recent-activity__bar--sentence ${width}`), bar('recent-activity__bar--time'));
};

const placeholderFeed = () => {
  const feed = document.createElement('div');
  feed.className = 'recent-activity__feed';

  const entries = document.createElement('ul');
  entries.className = 'recent-activity__entries list-unstyled mb-0';

  for (let index = 0; index < PLACEHOLDER_ROWS; index += 1) {
    const entry = document.createElement('li');
    entry.className = 'recent-activity__entry';
    const row = document.createElement('div');
    row.className = 'recent-activity__row d-flex align-items-start gap-2';
    skeletonizeRow(row, index);
    entry.appendChild(row);
    entries.appendChild(entry);
  }

  feed.appendChild(entries);
  return feed;
};

const skeletonizeCard = (card: HTMLElement) => {
  if (card.dataset.activitySkeleton === 'true') return;

  card.dataset.activitySkeleton = 'true';
  card.setAttribute('aria-busy', 'true');

  const feed = card.querySelector<HTMLElement>('.recent-activity__feed');
  if (feed) {
    // Un détail resté déplié au-dessus de barres n'aurait plus de ligne à détailler.
    feed.querySelectorAll<HTMLElement>('.recent-activity__detail').forEach((detail) => detail.remove());
    feed.querySelectorAll<HTMLElement>('.recent-activity__row').forEach(skeletonizeRow);
    // Les intertitres de jour changent avec les types retenus : les laisser lisibles au-dessus
    // de lignes fantômes annoncerait des jours que la réponse va peut-être démentir.
    feed.querySelectorAll<HTMLElement>('.recent-activity__day-row').forEach((day) => {
      day.replaceChildren(bar('recent-activity__bar--day'));
      // Un bouton de repli sans libellé ni chevron : plus rien à replier le temps du chargement.
      day.setAttribute('inert', '');
    });
    return;
  }

  card.querySelector('.recent-activity__empty')?.replaceWith(placeholderFeed());
};

export function initializeRecentActivitySkeleton() {
  if ((window as any).__recentActivitySkeletonInitialized) return;
  (window as any).__recentActivitySkeletonInitialized = true;

  // `turbo:click` plutôt qu'un écouteur de clic : Turbo l'émet pour tous les liens qu'il prend
  // en charge, y compris ceux qui se chargent dans une frame — c'est justement l'intercepteur
  // de la frame qui annule ensuite l'événement pour piloter la navigation lui-même.
  document.addEventListener('turbo:click', (event) => {
    const target = event.target as Element | null;
    const kind = target?.closest<HTMLElement>(KIND_SELECTOR);
    if (!kind) return;

    const card = kind.closest<HTMLElement>(CARD_SELECTOR);
    if (!card) return;

    markKindToggled(kind);
    skeletonizeCard(card);
  });
}
